/**
 * TenantUserChangeIdentifierModal — ADR-0022 tenant scope.
 *
 * Tenant-admin (uprawnienie users.change_identifier) zmienia email i/lub login
 * innemu tenant userowi. Brak password challenge — to admin operacja, audit
 * zachowuje ślad.
 *
 * Effects po sukcesie (BE): zmiana email/username, revoke wszystkich sesji
 * target usera, anti-takeover mail (stary + nowy gdy email się zmienił).
 *
 * UI:
 *  - Pre-filled email + username z aktualnymi wartościami.
 *  - Submit disabled gdy żadna z wartości się nie zmieniła.
 *  - Inline błędy: 409 EMAIL_ALREADY_EXISTS_IN_TENANT, USERNAME_ALREADY_EXISTS_IN_TENANT.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isValidUsername } from '@saas/shared';
import type { AxiosError } from 'axios';

import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export interface TenantUserChangeIdentifierModalProps {
  open: boolean;
  onClose: () => void;
  user: { id: string; email: string; username: string | null; displayName: string | null } | null;
  onSuccess: () => void;
}

export function TenantUserChangeIdentifierModal({
  open,
  onClose,
  user,
  onSuccess,
}: TenantUserChangeIdentifierModalProps) {
  const { t } = useTranslation();
  const toast = useToast();

  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [apiError, setApiError] = useState<string | null>(null);

  useEffect(() => {
    if (open && user) {
      setEmail(user.email);
      setUsername(user.username ?? '');
      setFieldErrors({});
      setApiError(null);
      setSubmitting(false);
    }
  }, [open, user]);

  if (!user) return null;

  function validate(): boolean {
    if (!user) return false;
    const errors: Record<string, string> = {};
    const e = email.trim().toLowerCase();
    const u = username.trim();

    if (!e) errors.email = t('tenant.users.changeIdentifier.errors.emailRequired');
    else if (!EMAIL_REGEX.test(e))
      errors.email = t('tenant.users.changeIdentifier.errors.emailFormat');

    if (u && !isValidUsername(u))
      errors.username = t('tenant.users.changeIdentifier.errors.usernameFormat');

    if (e === user.email && u === (user.username ?? '')) {
      errors.username = t('tenant.users.changeIdentifier.errors.noChange');
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }

  async function handleSubmit() {
    if (!user) return;
    if (!validate()) return;
    setSubmitting(true);
    setApiError(null);

    const payload: Record<string, string> = {};
    const e = email.trim().toLowerCase();
    const u = username.trim();
    if (e !== user.email) payload.email = e;
    if (u && u !== (user.username ?? '')) payload.username = u;

    try {
      await api.post(`/users/${user.id}/change-identifier`, payload);
      toast.show({
        variant: 'success',
        title: t('tenant.users.changeIdentifier.savedTitle'),
        description: t('tenant.users.changeIdentifier.savedDescription'),
      });
      onSuccess();
      onClose();
    } catch (err) {
      const axErr = err as AxiosError<{ error: string }>;
      const code = axErr.response?.data?.error;
      // BE konwencja: bare codes dla single-DB checks (change-identifier sprawdza
      // tylko tenant.users). Suffix _IN_TENANT/_IN_ADM zarezerwowany dla invite
      // route ktory sprawdza obie bazy.
      if (code === 'EMAIL_ALREADY_EXISTS') {
        setFieldErrors((prev) => ({
          ...prev,
          email: t('tenant.users.changeIdentifier.errors.emailTaken'),
        }));
      } else if (code === 'USERNAME_ALREADY_EXISTS') {
        setFieldErrors((prev) => ({
          ...prev,
          username: t('tenant.users.changeIdentifier.errors.usernameTaken'),
        }));
      } else if (axErr.response?.status === 403) {
        setApiError(t('tenant.users.changeIdentifier.errors.forbidden'));
      } else {
        setApiError(t('tenant.users.changeIdentifier.errors.unknown'));
      }
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={() => (submitting ? undefined : onClose())}
      title={t('tenant.users.changeIdentifier.title')}
      description={t('tenant.users.changeIdentifier.description', {
        name: user.displayName ?? user.email,
      })}
      size="md"
      closeOnBackdrop={!submitting}
      closeOnEsc={!submitting}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={() => void handleSubmit()}
            loading={submitting}
            disabled={submitting}
            data-testid="tenant-change-identifier-submit"
          >
            {submitting
              ? t('tenant.users.changeIdentifier.submitting')
              : t('tenant.users.changeIdentifier.submit')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:bg-amber-900/30 dark:text-amber-200">
          {t('tenant.users.changeIdentifier.warning')}
        </p>
        <Input
          label={t('tenant.users.changeIdentifier.emailLabel')}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={fieldErrors.email}
          disabled={submitting}
          data-testid="tenant-change-identifier-email"
        />
        <Input
          label={t('tenant.users.changeIdentifier.usernameLabel')}
          type="text"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          error={fieldErrors.username}
          hint={t('tenant.users.changeIdentifier.usernameHint')}
          disabled={submitting}
          data-testid="tenant-change-identifier-username"
        />
        {apiError && (
          <div
            role="alert"
            className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300"
            data-testid="tenant-change-identifier-error"
          >
            {apiError}
          </div>
        )}
      </div>
    </Modal>
  );
}
